import React from 'react';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaLock, FaUserShield, FaDatabase, FaCookie } from 'react-icons/fa';

const PrivacyPolicy = () => {
    return (
        <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-purple-50">
            {/* Header */}
            <header className="bg-white shadow-sm border-b border-gray-200">
                <div className="container mx-auto px-4 py-4">
                    <Link to="/" className="inline-flex items-center text-pink-600 hover:text-pink-700 font-semibold transition-colors">
                        <FaArrowLeft className="mr-2" />
                        Back to Home
                    </Link>
                </div>
            </header>

            {/* Content */}
            <div className="container mx-auto px-4 py-12 max-w-4xl">
                <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
                    {/* Title */}
                    <div className="flex items-center mb-8">
                        <FaLock className="text-4xl text-pink-600 mr-4" />
                        <div>
                            <h1 className="text-4xl font-black text-gray-900">Privacy Policy</h1>
                            <p className="text-gray-600 mt-1">Last updated: December 4, 2025</p>
                        </div>
                    </div>

                    {/* Introduction */}
                    <div className="bg-pink-50 border-l-4 border-pink-500 p-6 mb-8 rounded-r-lg">
                        <p className="text-gray-700 leading-relaxed">
                            At SoulMate, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our matrimonial service. Please read this privacy policy carefully.
                        </p>
                    </div>

                    {/* Content Sections */}
                    <div className="space-y-8 text-gray-700">
                        <section>
                            <div className="flex items-center mb-4">
                                <FaDatabase className="text-2xl text-pink-600 mr-3" />
                                <h2 className="text-2xl font-bold text-gray-900">1. Information We Collect</h2>
                            </div>

                            <h3 className="text-xl font-semibold text-gray-800 mt-4 mb-2">Personal Information</h3>
                            <p className="leading-relaxed mb-3">
                                We collect information that you provide directly to us, including:
                            </p>
                            <ul className="list-disc list-inside space-y-2 ml-4">
                                <li>Name, age, date of birth, and gender</li>
                                <li>Contact information (email, phone number, address)</li>
                                <li>Profile photos and personal descriptions</li>
                                <li>Educational and professional details</li>
                                <li>Family information and background</li>
                                <li>Religious and cultural preferences</li>
                                <li>Hobbies and interests</li>
                                <li>Partner preferences and expectations</li>
                            </ul>

                            <h3 className="text-xl font-semibold text-gray-800 mt-4 mb-2">Automatically Collected Information</h3>
                            <ul className="list-disc list-inside space-y-2 ml-4">
                                <li>IP address and device information</li>
                                <li>Browser type and operating system</li>
                                <li>Pages visited and time spent on platform</li>
                                <li>Referring website addresses</li>
                                <li>Location data (with your permission)</li>
                            </ul>
                        </section>

                        <section>
                            <div className="flex items-center mb-4">
                                <FaUserShield className="text-2xl text-pink-600 mr-3" />
                                <h2 className="text-2xl font-bold text-gray-900">2. How We Use Your Information</h2>
                            </div>
                            <p className="leading-relaxed mb-3">
                                We use the information we collect to:
                            </p>
                            <ul className="list-disc list-inside space-y-2 ml-4">
                                <li>Create and manage your account</li>
                                <li>Provide personalized match recommendations</li>
                                <li>Facilitate communication between users</li>
                                <li>Process payments and manage subscriptions</li>
                                <li>Send you updates, newsletters, and promotional materials</li>
                                <li>Improve our services and user experience</li>
                                <li>Detect and prevent fraud or abuse</li>
                                <li>Comply with legal obligations</li>
                                <li>Conduct research and analytics</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Information Sharing and Disclosure</h2>

                            <h3 className="text-xl font-semibold text-gray-800 mt-4 mb-2">With Other Users</h3>
                            <p className="leading-relaxed mb-3">
                                Your profile information is visible to other registered users based on your privacy settings. You can control what information is shared through your account settings.
                            </p>

                            <h3 className="text-xl font-semibold text-gray-800 mt-4 mb-2">With Service Providers</h3>
                            <p className="leading-relaxed mb-3">
                                We may share your information with third-party service providers who perform services on our behalf, such as:
                            </p>
                            <ul className="list-disc list-inside space-y-2 ml-4">
                                <li>Payment processors</li>
                                <li>Email service providers</li>
                                <li>Analytics providers</li>
                                <li>Customer support tools</li>
                                <li>Cloud storage providers</li>
                            </ul>

                            <h3 className="text-xl font-semibold text-gray-800 mt-4 mb-2">Legal Requirements</h3>
                            <p className="leading-relaxed">
                                We may disclose your information if required by law, court order, or governmental request, or to protect our rights, property, or safety.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Data Security</h2>
                            <p className="leading-relaxed mb-3">
                                We implement appropriate technical and organizational measures to protect your personal information, including:
                            </p>
                            <ul className="list-disc list-inside space-y-2 ml-4">
                                <li>SSL/TLS encryption for data transmission</li>
                                <li>Encrypted storage of sensitive data</li>
                                <li>Regular security audits and updates</li>
                                <li>Access controls and authentication</li>
                                <li>Employee training on data protection</li>
                            </ul>
                            <p className="leading-relaxed mt-3">
                                However, no method of transmission over the Internet is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
                            </p>
                        </section>

                        <section>
                            <div className="flex items-center mb-4">
                                <FaCookie className="text-2xl text-pink-600 mr-3" />
                                <h2 className="text-2xl font-bold text-gray-900">5. Cookies and Tracking Technologies</h2>
                            </div>
                            <p className="leading-relaxed mb-3">
                                We use cookies and similar tracking technologies to:
                            </p>
                            <ul className="list-disc list-inside space-y-2 ml-4">
                                <li>Remember your preferences and settings</li>
                                <li>Understand how you use our service</li>
                                <li>Provide personalized content and ads</li>
                                <li>Analyze site traffic and trends</li>
                            </ul>
                            <p className="leading-relaxed mt-3">
                                You can control cookies through your browser settings, but disabling cookies may limit your ability to use certain features.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Your Privacy Rights</h2>
                            <p className="leading-relaxed mb-3">
                                You have the right to:
                            </p>
                            <ul className="list-disc list-inside space-y-2 ml-4">
                                <li><strong>Access:</strong> Request a copy of your personal data</li>
                                <li><strong>Rectification:</strong> Correct inaccurate or incomplete data</li>
                                <li><strong>Deletion:</strong> Request deletion of your account and data</li>
                                <li><strong>Portability:</strong> Receive your data in a structured format</li>
                                <li><strong>Objection:</strong> Object to processing of your data</li>
                                <li><strong>Restriction:</strong> Request restriction of data processing</li>
                                <li><strong>Withdraw Consent:</strong> Withdraw consent at any time</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Data Retention</h2>
                            <p className="leading-relaxed">
                                We retain your personal information for as long as necessary to provide our services and comply with legal obligations. When you delete your account, we will delete or anonymize your information within 90 days, except where we are required to retain it by law.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Children's Privacy</h2>
                            <p className="leading-relaxed">
                                Our service is intended for users aged 18 and above. We do not knowingly collect information from individuals under 18. If we become aware that we have collected information from a child under 18, we will take steps to delete such information.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. International Data Transfers</h2>
                            <p className="leading-relaxed">
                                Your information may be transferred to and processed in countries other than your country of residence. We ensure appropriate safeguards are in place to protect your information in accordance with this Privacy Policy.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Changes to Privacy Policy</h2>
                            <p className="leading-relaxed">
                                We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Last updated" date. We encourage you to review this Privacy Policy periodically.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Contact Us</h2>
                            <p className="leading-relaxed mb-4">
                                If you have any questions about this Privacy Policy or our data practices, please contact us:
                            </p>
                            <div className="p-6 bg-gradient-to-r from-pink-50 to-purple-50 rounded-lg border border-pink-200">
                                <p className="font-semibold text-gray-800"><strong>Email:</strong> Support@kalyanamatrimony.com</p>
                                <p className="font-semibold text-gray-800 mt-2"><strong>Phone:</strong> +91 744-8539858</p>
                                <p className="font-semibold text-gray-800 mt-2"><strong>Address:</strong> 573, Softye Technologies, Mettur main road, Ammapettai - Erode</p>
                            </div>
                        </section>
                    </div>

                    {/* Footer */}
                    <div className="mt-12 pt-8 border-t border-gray-200 text-center">
                        <p className="text-gray-600 mb-4">
                            By using SoulMate, you consent to the collection and use of your information as described in this Privacy Policy.
                        </p>
                        <Link to="/" className="inline-block px-6 py-3 bg-gradient-to-r from-pink-500 to-purple-500 text-white font-semibold rounded-lg hover:shadow-lg transition-all">
                            Back to Home
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PrivacyPolicy;
